import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { getDiffBetweenDates } from 'odfjell-date-time-utils/dateTimeUtils';
import useEvent from '../../app/useEvent';
import useEventType from '../../app/useEventType';
import useRotation from '../../app/useRotation';
import { InputDataTypeEnum } from '../../enums/inputDataTypeEnum';
import { RotationTreeEnum } from '../../enums/rotationTreeEnum';
import DropdownGroupRegFactory from '../../factory/DropdownGroupRegFactory';
import { BerthCall } from '../../types/ViewModel/BerthCall';
import { CargoOperation } from '../../types/ViewModel/CargoOperation';
import { DropdownGroupReg } from '../../types/ViewModel/DropdownGroupReg';
import { EventDate } from '../../types/ViewModel/EventDate';
import { EventTypeData } from '../../types/ViewModel/EventTypeData';
import { EventTypeInput } from '../../types/ViewModel/EventTypeInput';
import { EventTypeInputReg } from '../../types/ViewModel/EventTypeInputReg';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import { PortCall } from '../../types/ViewModel/PortCall';
import { Rotation } from '../../types/ViewModel/Rotation';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import eventtypecommon from './eventtypecommon';
import eventtypehandler from './eventtypehandler';
import { resolveHeading } from './headingOverrides';

export default () => {
    const { createEventName } = eventtypehandler();
    const { getPortCallFromNode, getCargoOperation } = useRotation();
    const { isCargoDropdown } = useEvent();
    const { getEventTypeFromEvent } = useEventType();
    const { getMovedDeletedStylingClass, hasInputGroups } = eventtypecommon();
    const { getDropdownGroupRegFromDropdownGroup } = DropdownGroupRegFactory();

    const getDropDownHtmlSameLine = (dropdownGroupReg: DropdownGroupReg): string => {
        let first = true;
        let eventInputsHtml = '';
        dropdownGroupReg.dropdownItemRegList.forEach((dropdownItemReg) => {
            if (!first) {
                eventInputsHtml += ', ';
                eventInputsHtml += dropdownItemReg.dropdownItemValue;
            } else {
                eventInputsHtml += dropdownItemReg.dropdownItemValue;
            }
            first = false;
        });
        return eventInputsHtml;
    };

    const getCargoDropDownHtml = (selectedNode: IRotationNode, dropdownGroupReg: DropdownGroupReg, ev: TankersEvent): string => {
        let eventInputsHtml = '';
        dropdownGroupReg.dropdownItemRegList.forEach((dropdownItemReg) => {
            if (selectedNode?.type !== RotationTreeEnum.CargoOperation) {
                let cargoOp: CargoOperation | undefined = undefined;
                let notMovedOrDeleted = true;
                const eventLevel = ev.getEventLevel();
                if (eventLevel === RotationTreeEnum.PortCall) {
                    if (selectedNode?.type === RotationTreeEnum.PortCall) {
                        const pc = selectedNode as PortCall;
                        if (pc) {
                            const cargoOperationArray = pc.berthCallArray.flatMap((o) => o.cargoOperationArray);
                            cargoOp = cargoOperationArray.find((co) => co.cargoOperationId === dropdownItemReg.dropdownItemId);
                            if (cargoOp) {
                                notMovedOrDeleted = !cargoOp.isDeleted;
                            }
                        }
                    } else if (selectedNode?.type === RotationTreeEnum.BerthCall) {
                        const bc = selectedNode as BerthCall;
                        if (bc) {
                            const foundCargo = bc.cargoOperationArray.find((co) => co.cargoOperationId === dropdownItemReg.dropdownItemId);
                            if (foundCargo) {
                                cargoOp = foundCargo;
                                notMovedOrDeleted = !cargoOp.isDeleted;
                            }
                        }
                    }
                } else if (eventLevel === RotationTreeEnum.BerthCall && selectedNode?.type === RotationTreeEnum.BerthCall) {
                    const bc = selectedNode as BerthCall;
                    if (bc) {
                        const foundCargo = bc.cargoOperationArray.find((co) => co.cargoOperationId === dropdownItemReg.dropdownItemId);
                        if (foundCargo) {
                            cargoOp = foundCargo;
                            notMovedOrDeleted = !cargoOp.isMoved && !cargoOp.isDeleted;
                        }
                    }
                }
                if (cargoOp !== undefined) {
                    if (dropdownItemReg.isValid && notMovedOrDeleted) {
                        eventInputsHtml += '<span class="cargo-name">' + dropdownItemReg.dropdownItemValue + '</span>';
                    } else {
                        const stylingClass = getMovedDeletedStylingClass(RotationTreeEnum.CargoOperation, cargoOp?.isDeleted);
                        eventInputsHtml += '<span class="cargo-name moved' + stylingClass + '">' + dropdownItemReg.dropdownItemValue + '</span>';
                    }
                }
            }
        });
        return eventInputsHtml;
    };

    const sortEventTypeInput = (event: TankersEvent): EventTypeInput[] => {
        const evType = getEventTypeFromEvent(event);
        if (hasInputGroups(evType)) {
            const evTypeInpList: EventTypeInput[] = [];
            evType?.eventTypeInputGroupArray?.forEach((groupElem) => {
                const groupItemList = groupElem.eventTypeInputGroupItemArray.sort((a, b) => (a.itemOrder > b.itemOrder ? 1 : -1));
                groupItemList.forEach((o) => {
                    const evTypeInput = evType?.eventTypeInputArray?.find((evTypeInp) => evTypeInp.eventTypeInputId === o.eventTypeInputId) ?? null;
                    if (evTypeInput) {
                        evTypeInpList.push(evTypeInput);
                    }
                });
            });
            return evTypeInpList;
        }
        return event.eventType?.eventTypeInputArray?.sort((a, b) => (a.inputOrder > b.inputOrder ? 1 : -1));
    };

    const createDropdownInputHtml = (
        selectedNode: IRotationNode,
        eventTypeInputItem: EventTypeInput,
        evTypeInputReg: EventTypeInputReg,
        ev: TankersEvent
    ): string => {
        const dropdownGroupReg = getDropdownGroupRegFromDropdownGroup(
            eventTypeInputItem.dropdownGroup,
            evTypeInputReg.dropdownGroupReg?.dropdownItemRegList
        );
        if (evTypeInputReg.dropdownGroupReg && isCargoDropdown(eventTypeInputItem.dropdownGroup?.dropdownTypeId)) {
            if (selectedNode.type !== RotationTreeEnum.CargoOperation) {
                const heading = resolveHeading(eventTypeInputItem);
                return '<h5>' + heading + ':</h5>' + getCargoDropDownHtml(selectedNode, dropdownGroupReg, ev);
            }
        } else {
            const heading = resolveHeading(eventTypeInputItem);
            return '<span class="dropdown-item-name">' + heading + ': ' + getDropDownHtmlSameLine(dropdownGroupReg) + '</span>';
        }
        return '';
    };

    const getEventInputsHtmlDropdown = (selectedNode: IRotationNode, ev: TankersEvent): string => {
        let eventInputsHtml = '';
        if (ev.eventType?.eventTypeInputArray && ev.eventType?.eventTypeInputArray.length > 0) {
            const evTypeInpSorted = sortEventTypeInput(ev);
            evTypeInpSorted.forEach((eventTypeInputItem) => {
                const evTypeInputReg = ev.eventTypeInputRegList?.find((eigra) => {
                    return eigra.eventTypeInputId === eventTypeInputItem.eventTypeInputId && eigra.inputDataTypeId === InputDataTypeEnum.Dropdown;
                });
                if (evTypeInputReg) {
                    eventInputsHtml += createDropdownInputHtml(selectedNode, eventTypeInputItem, evTypeInputReg, ev);
                }
            });
        }
        return eventInputsHtml;
    };

    const createStringInputHtml = (eventTypeInputItem: EventTypeInput, evTypeInputReg: EventTypeInputReg): string => {
        if (evTypeInputReg.stringValueReg)
            return '<span class="dropdown-item-name">' + eventTypeInputItem.inputHeading + ': ' + evTypeInputReg.stringValueReg + '</span>';
        return '';
    };

    const createDatetimeInputHtml = (eventTypeInputItem: EventTypeInput, evTypeInputReg: EventTypeInputReg): string => {
        if (evTypeInputReg.datetimeValueRegDisplay)
            return '<span class="dropdown-item-name">' + eventTypeInputItem.inputHeading + ': ' + evTypeInputReg.datetimeValueRegDisplay + '</span>';
        return '';
    };

    const createIntInputHtml = (eventTypeInputItem: EventTypeInput, evTypeInputReg: EventTypeInputReg): string => {
        if (evTypeInputReg.intValueRegStr)
            return '<span class="dropdown-item-name">' + eventTypeInputItem.inputHeading + ': ' + evTypeInputReg.intValueRegStr + '</span>';
        return '';
    };

    const createDecimalInputHtml = (eventTypeInputItem: EventTypeInput, evTypeInputReg: EventTypeInputReg): string => {
        if (evTypeInputReg.decimalValueRegStr)
            return '<span class="dropdown-item-name">' + eventTypeInputItem.inputHeading + ': ' + evTypeInputReg.decimalValueRegStr + '</span>';
        return '';
    };

    const createStars = (x: number): string => {
        if (x === 1) return '*';
        if (x === 2) return '**';
        if (x === 3) return '***';
        if (x === 4) return '****';
        if (x === 5) return '*****';
        return '';
    };

    const createRatingInputHtml = (selectedNode: IRotationNode, eventTypeInputItem: EventTypeInput, evTypeInputReg: EventTypeInputReg): string => {
        if (evTypeInputReg.ratingReg) {
            let str = '';
            if (evTypeInputReg.ratingReg.ratingComment && evTypeInputReg.ratingReg.ratingComment.length > 0)
                str = ' (' + evTypeInputReg.ratingReg.ratingComment + ')';
            return (
                '<span class="dropdown-item-name">' +
                eventTypeInputItem.inputHeading +
                ': ' +
                createStars(evTypeInputReg.ratingReg.ratingValue) +
                str +
                '</span>'
            );
        }
        return '';
    };

    const getEventInputsHtmlAllInputs = (selectedNode: IRotationNode, ev: TankersEvent): string => {
        let eventInputsHtml = '';
        if (ev.eventType?.eventTypeInputArray && ev.eventType?.eventTypeInputArray.length > 0) {
            const evTypeInpSorted = sortEventTypeInput(ev);
            evTypeInpSorted.forEach((eventTypeInputItem) => {
                const evTypeInputReg = ev.eventTypeInputRegList?.find((eigra) => {
                    return eigra.eventTypeInputId === eventTypeInputItem.eventTypeInputId;
                });
                if (evTypeInputReg) {
                    switch (eventTypeInputItem.inputDataTypeId) {
                        case InputDataTypeEnum.String:
                            eventInputsHtml += createStringInputHtml(eventTypeInputItem, evTypeInputReg);
                            break;
                        case InputDataTypeEnum.Datetime:
                            eventInputsHtml += createDatetimeInputHtml(eventTypeInputItem, evTypeInputReg);
                            break;
                        case InputDataTypeEnum.Int:
                            eventInputsHtml += createIntInputHtml(eventTypeInputItem, evTypeInputReg);
                            break;
                        case InputDataTypeEnum.Decimal:
                            eventInputsHtml += createDecimalInputHtml(eventTypeInputItem, evTypeInputReg);
                            break;
                        case InputDataTypeEnum.Rating:
                            eventInputsHtml += createRatingInputHtml(selectedNode, eventTypeInputItem, evTypeInputReg);
                            break;
                        case InputDataTypeEnum.Dropdown:
                            eventInputsHtml += createDropdownInputHtml(selectedNode, eventTypeInputItem, evTypeInputReg, ev);
                            break;
                    }
                }
            });
        }
        return eventInputsHtml;
    };

    const getActivity = (
        rotation: Rotation,
        fullEventArray: TankersEvent[],
        selectedNode: IRotationNode,
        eventTypeData: EventTypeData,
        event: TankersEvent,
        allInputItems: boolean
    ): string => {
        let activityString = '';
        const portCall = getPortCallFromNode(selectedNode);
        const evLevel = event.getEventLevel();
        switch (evLevel) {
            case RotationTreeEnum.Sea:
            case RotationTreeEnum.PortCall: {
                activityString = createEventName(fullEventArray, event, eventTypeData, selectedNode);
                break;
            }
            case RotationTreeEnum.BerthCall: {
                if (selectedNode?.type === RotationTreeEnum.PortCall) {
                    activityString =
                        (portCall ? portCall.voyageNumberPresentationString : '???') +
                        ' ' +
                        '' + //this.getBerthName(event) + ' - '
                        createEventName(fullEventArray, event, eventTypeData, selectedNode);
                } else {
                    activityString = createEventName(fullEventArray, event, eventTypeData, selectedNode);
                }
                break;
            }
            case RotationTreeEnum.CargoOperation: {
                const cargoOp = getCargoOperation(rotation, event.portCallId, event.berthCallId, event.cargoOperationId);

                if (selectedNode?.type === RotationTreeEnum.PortCall) {
                    activityString = //getBerthName(event) + ' - ' +
                        (cargoOp ? cargoOp?.cargoNumber : '???') +
                        ' ' +
                        (cargoOp ? cargoOp?.tradeName : '???') +
                        ' - ' +
                        createEventName(fullEventArray, event, eventTypeData, selectedNode);
                } else if (selectedNode?.type === RotationTreeEnum.BerthCall) {
                    activityString =
                        (cargoOp ? cargoOp.cargoNumber : '???') +
                        ' ' +
                        (cargoOp ? cargoOp.tradeName : '???') +
                        ' - ' +
                        createEventName(fullEventArray, event, eventTypeData, selectedNode);
                } else {
                    activityString = createEventName(fullEventArray, event, eventTypeData, selectedNode);
                }
                break;
            }
        }
        if (allInputItems) activityString += '<span class="ext-info">' + getEventInputsHtmlAllInputs(selectedNode, event) + '</span>';
        else activityString += '<span class="ext-info">' + getEventInputsHtmlDropdown(selectedNode, event) + '</span>';
        return activityString;
    };

    const getUtcOffset = (evDate: EventDate | null): string => {
        if (evDate && evDate.actualDateTimeLocal && evDate.actualDateTimeUtc) {
            const start = evDate.actualDateTimeUtc;
            const end = evDate.actualDateTimeLocal;
            const min = getDiffBetweenDates(end, start, 'minute').minutes;
            const minInDec = min / 60;
            if (minInDec > 0) return 'UTC+' + minInDec.toString();
            else if (minInDec < 0) return 'UTC' + minInDec.toString();
            else return 'UTC';
        }
        return '';
    };

    const getEventDate = (date: Date | null): string => {
        if (date) {
            return prettifyDateTimeHHmm(date);
        }
        return 'No date';
    };

    return {
        getActivity,
        getUtcOffset,
        getEventDate,
    };
};
